import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AppLocale } from '@/i18n/config';
import { usePendingWrite } from '@/lib/use-pending-write';
import type { FinanceMovement } from './dashboard-model';
import {
  createEmptyForecastForm,
  forecastValuesFromMovement,
  submitValidForecast,
  validateForecastForm,
  type ForecastFormErrors,
  type ForecastFormMode,
  type ForecastFormValues,
} from './forecast-form-model';

type UseForecastFormOptions = {
  locale: AppLocale;
  selectedYear: number;
  reload: () => Promise<void>;
};

export function useForecastForm({ locale, selectedYear, reload }: UseForecastFormOptions) {
  const { t } = useTranslation('finance');
  const [mode, setMode] = useState<ForecastFormMode>(null);
  const [editingMovementId, setEditingMovementId] = useState<string | null>(null);
  const [values, setValues] = useState<ForecastFormValues>(() => createEmptyForecastForm());
  const [errors, setErrors] = useState<ForecastFormErrors>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const { pending, run } = usePendingWrite();

  const clearForm = useCallback(() => {
    setEditingMovementId(null);
    setValues(createEmptyForecastForm());
    setErrors({});
    setApiError(null);
    setSaved(false);
  }, []);

  const reset = useCallback(() => {
    setMode(null);
    clearForm();
  }, [clearForm]);

  useEffect(() => {
    if (mode !== 'edit') {
      setValues(createEmptyForecastForm());
      setErrors({});
    }
  }, [mode, selectedYear]);

  const openCreate = () => {
    setMode('create');
    clearForm();
  };

  const openEdit = (movement: FinanceMovement) => {
    setMode('edit');
    setEditingMovementId(movement.id);
    setValues(forecastValuesFromMovement(movement, locale));
    setErrors({});
    setApiError(null);
    setSaved(false);
  };

  const setField = <K extends keyof ForecastFormValues>(key: K, value: ForecastFormValues[K]) => {
    setSaved(false);
    setValues((previous) => ({ ...previous, [key]: value }));
  };

  const submit = async () => {
    const validation = validateForecastForm(values, locale, t);

    setErrors(validation.errors);
    setApiError(null);
    setSaved(false);

    if (validation.parsed === null) {
      return;
    }

    const failureMessage = await submitValidForecast({
      editingMovementId,
      mode,
      values,
      parsed: validation.parsed,
      reload,
      t,
    });
    if (failureMessage !== null) {
      setApiError(failureMessage);
      return;
    }

    // `reset` clears the form and the flags; the confirmation is raised after it in the same batch.
    reset();
    setSaved(true);
  };

  return {
    mode,
    values,
    errors,
    apiError,
    saved,
    pending,
    reset,
    openCreate,
    openEdit,
    setField,
    submit: () => run(() => submit()),
  };
}

export type ForecastFormController = ReturnType<typeof useForecastForm>;
